import { useCollection } from "react-firebase-hooks/firestore";
import { collection, orderBy, query } from "firebase/firestore";
import { db } from "@/firebase/config";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Download, Globe, Calendar, User, Monitor } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface NewsletterSubscriber {
  email: string;
  createdAt: {
    toDate: () => Date;
  };
  source: string;
  subscriptionPage: string;
  userAgent: string;
}

export default function Subscribers() {
  const [value, loading, error] = useCollection(
    query(collection(db, "NewsLetterWeb"), orderBy("createdAt", "desc"))
  );

  const handleExportCSV = () => {
    if (!value?.docs.length) return;

    const csvContent = [
      ["Email", "Created At", "Source", "Subscription Page", "User Agent"],
      ...value.docs.map((doc) => {
        const data = doc.data() as NewsletterSubscriber;
        return [
          data.email,
          format(data.createdAt.toDate(), "yyyy-MM-dd HH:mm:ss"),
          data.source,
          data.subscriptionPage,
          data.userAgent,
        ];
      }),
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter-subscribers-${format(
      new Date(),
      "yyyy-MM-dd"
    )}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertDescription>
          Error fetching subscribers: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading text-forest-green">
            Newsletter Subscribers
          </h1>
          <p className="text-gray-600 mt-1">
            All newsletter subscriptions from NewsLetterWeb collection
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleExportCSV}
          disabled={loading || !value?.docs.length}
        >
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-md border border-misty-sage overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-misty-sage/20">
                <th className="px-6 py-3 text-left text-sm font-medium text-forest-green">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Email
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-forest-green">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Created At
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-forest-green">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Source
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-forest-green">
                  Subscription Page
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-forest-green">
                  <div className="flex items-center gap-2">
                    <Monitor className="w-4 h-4" />
                    User Agent
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-misty-sage">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4">
                      <Skeleton className="h-4 w-48" />
                    </td>
                    <td className="px-6 py-4">
                      <Skeleton className="h-4 w-32" />
                    </td>
                    <td className="px-6 py-4">
                      <Skeleton className="h-4 w-24" />
                    </td>
                    <td className="px-6 py-4">
                      <Skeleton className="h-4 w-16" />
                    </td>
                    <td className="px-6 py-4">
                      <Skeleton className="h-4 w-64" />
                    </td>
                  </tr>
                ))
              ) : value?.empty ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No newsletter subscribers found
                  </td>
                </tr>
              ) : (
                value?.docs.map((doc) => {
                  const subscriber = doc.data() as NewsletterSubscriber;
                  return (
                    <tr key={doc.id} className="hover:bg-misty-sage/5">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="text-copper-brown font-medium">
                            {subscriber.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        <div className="text-sm">
                          {format(subscriber.createdAt.toDate(), "MMM d, yyyy")}
                        </div>
                        <div className="text-xs text-gray-400">
                          {format(subscriber.createdAt.toDate(), "h:mm a")}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-forest-green/10 text-forest-green">
                          {subscriber.source}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm">
                        <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                          {subscriber.subscriptionPage}
                        </code>
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-xs max-w-xs">
                        <div className="truncate" title={subscriber.userAgent}>
                          {subscriber.userAgent}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Statistics Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-md border border-misty-sage">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Subscribers
              </p>
              <p className="text-2xl font-bold text-forest-green">
                {loading ? "..." : value?.docs.length || 0}
              </p>
            </div>
            <User className="h-8 w-8 text-forest-green/60" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-misty-sage">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                From Website Footer
              </p>
              <p className="text-2xl font-bold text-forest-green">
                {loading
                  ? "..."
                  : value?.docs.filter(
                      (doc) =>
                        (doc.data() as NewsletterSubscriber).source ===
                        "website_footer"
                    ).length || 0}
              </p>
            </div>
            <Globe className="h-8 w-8 text-forest-green/60" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-misty-sage">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Latest Subscription
              </p>
              <p className="text-sm font-medium text-forest-green">
                {loading
                  ? "..."
                  : value?.docs.length
                  ? format(
                      (
                        value.docs[0].data() as NewsletterSubscriber
                      ).createdAt.toDate(),
                      "MMM d, yyyy"
                    )
                  : "None"}
              </p>
            </div>
            <Calendar className="h-8 w-8 text-forest-green/60" />
          </div>
        </div>
      </div>

      {/* Newsletter sending note */}
      <div className="mt-8 p-4 bg-misty-sage/10 rounded-lg">
        <p className="text-sm text-gray-500">
          💡 <strong>Newsletter Management:</strong> You can send newsletters to
          these subscribers from the "Create Newsletter" section. Each
          subscription includes detailed tracking information for better
          analytics.
        </p>
      </div>
    </div>
  );
}
